import { DebugMode } from './DebugMode.js';
import { Message } from './Message.js';
import { Status } from './Status.js';
import type { Tuple } from '../types/index.js';
import { noop } from '../utils/index.js';

export class Logger<const TData extends Tuple.Any> {
  #message: Message;

  #labels: Message.Labels;

  #data?: TData;

  constructor(message: Message, labels: Message.Labels, data?: TData) {
    this.#message = message;
    this.#labels = labels;
    if (data) this.#data = data;
  }

  /**
   * For general log messages. For readability, please consider using any of
   * the specific logging methods.
   *
   * @see verbose
   * @see info
   * @see success
   * @see warn
   * @see error
   */
  get log(): VoidFunction {
    return this.#createCallback(
      Status.info,
      Message.Type.Log,
      Message.Grouping.Ungrouped
    );
  }

  /**
   * For verbose/debugging messages, e.g., detailed information for diagnosing
   * issues. In most browsers, these messages are hidden by default and can be
   * enabled via developer tools.
   */
  get verbose() {
    return this.#createCallback(
      Status.verbose,
      Message.Type.Debug,
      Message.Grouping.NewLines
    );
  }

  /**
   * For informational messages, e.g. general information about the app state.
   *
   * **Important:** When logging data, you must provide a callback to preserve
   * the correct call stack in developer tools. This is due to a browser limitation
   * where console.groupCollapsed() doesn't preserve stack traces when called
   * through our abstraction layer.
   *
   * @example
   * // Without data - no callback needed
   * debug.message('Operation completed').info();
   *
   * @example
   * // With data - callback required
   * debug.message('Loaded items', items).info((log) => log());
   */
  get info() {
    return this.#createCallback(
      Status.info,
      Message.Type.Info,
      Message.Grouping.GroupCollapsed
    );
  }

  /**
   * For successful operations, e.g. tasks that completed without errors.
   *
   * **Important:** When logging data, you must provide a callback to preserve
   * the correct call stack in developer tools. This is due to a browser limitation
   * where console.groupCollapsed() doesn't preserve stack traces when called
   * through our abstraction layer.
   *
   * @example
   * // Without data - no callback needed
   * debug.message('Operation completed').success();
   *
   * @example
   * // With data - callback required
   * debug.message('Loaded items', items).success((log) => log());
   */
  get success() {
    return this.#createCallback(
      Status.success,
      Message.Type.Log,
      Message.Grouping.GroupCollapsed
    );
  }

  /**
   * For warning situations, e.g. potential issues that are not errors but
   * might require attention.
   */
  get warn() {
    return this.#createCallback(
      Status.warning,
      Message.Type.Warn,
      Message.Grouping.NewLines
    );
  }

  /** For error situations, e.g. problems that need immediate attention. */
  get error() {
    return this.#createCallback(
      Status.error,
      Message.Type.Error,
      Message.Grouping.NewLines
    );
  }

  #createCallback<
    const TMessageType extends Message.Type,
    const TMessageGrouping extends Message.Grouping.FromType<TMessageType>
  >(
    status: Status | null,
    logType: TMessageType,
    groupStyle: TMessageGrouping
  ): Logger.Callback<TData, TMessageType, TMessageGrouping> {
    const data = this.#data;

    if (!data?.length) {
      return Logger.#getBoundConsoleMethod(
        logType,
        ...Message.format(this.#message, status, this.#labels)
      );
    }

    if (
      groupStyle === Message.Grouping.Group ||
      groupStyle === Message.Grouping.GroupCollapsed
    ) {
      const collapsed = groupStyle === Message.Grouping.GroupCollapsed;
      const groupableLogType = logType as Message.Type.Groupable; // ensured by the constraint on TMessageGrouping

      return ((executor) => {
        this.#executeCallbacks(executor, status, groupableLogType, collapsed);
      }) as Logger.Callback<TData, TMessageType, TMessageGrouping>;
    }

    const [message, ...messageArgs] = Message.format(
      this.#message,
      status,
      this.#labels
    );

    const dataTemplate =
      groupStyle === Message.Grouping.NewLines ? '\n  %o' : '%o';

    return Logger.#getBoundConsoleMethod(
      logType,
      `${message}${dataTemplate.repeat(data.length)}`,
      ...messageArgs,
      ...data
    );
  }

  *#createGroupedCallbacks(
    status: Status | null,
    logType: Message.Type.Groupable,
    collapsed: boolean
  ): Generator<VoidFunction, void, void> {
    const message = Message.format(this.#message, status, this.#labels);

    if (!this.#data?.length) {
      yield Logger.#getBoundConsoleMethod(logType, ...message);
      return;
    }

    yield Logger.#getBoundConsoleMethod(
      collapsed ? 'groupCollapsed' : 'group',
      ...message
    );

    for (const data of this.#data) {
      yield Logger.#getBoundConsoleMethod(logType, data);
    }

    yield Logger.#getBoundConsoleMethod('groupEnd');
  }

  #executeCallbacks(
    executor: Logger.Callback.Group.Executor,
    status: Status | null,
    logType: Message.Type.Groupable,
    collapsed: boolean
  ) {
    const callbacks = this.#createGroupedCallbacks(status, logType, collapsed);

    for (const callback of callbacks) executor(callback);
  }

  static #getBoundConsoleMethod<const TMethod extends keyof Console>(
    method: TMethod,
    ...args: ReadonlyArray<unknown>
  ) {
    try {
      if (DebugMode.shouldSkipLogging(...args)) {
        return noop as Console[TMethod];
      }

      return Function.prototype.bind.call(
        console[method],
        console,
        ...args
      ) as Console[TMethod];
    } catch (error) {
      // Fallback if console method binding fails:
      console.error('Debug logging error:', error);
      return noop as Console[TMethod];
    }
  }
}

export namespace Logger {
  export type Callback<
    TData extends Tuple.Any,
    TMessageType extends Message.Type,
    TMessageGrouping extends Message.Grouping.FromType<TMessageType>
  > =
    HasData<TData> extends true
      ? RequiresGroup<TMessageGrouping> extends true
        ? Callback.Group
        : Callback.Single
      : Callback.Single;

  export namespace Callback {
    export type Single = VoidFunction;

    export type Group = (executor: Group.Executor) => void;

    export namespace Group {
      export type Executor = (log: VoidFunction) => void;
    }
  }

  export class Void extends Logger<never> {
    static #instance?: Void;

    static get instance() {
      return (this.#instance ??= new this());
    }

    private constructor() {
      super('', ['', []] as const);
    }

    override get log() {
      return noop as never;
    }

    override get verbose() {
      return noop as never;
    }

    override get info() {
      return noop as never;
    }

    override get success() {
      return noop as never;
    }

    override get warn() {
      return noop as never;
    }

    override get error() {
      return noop as never;
    }
  }
}

/** Helper type for improved readability. */
type HasData<TData> = TData extends Tuple.NonEmpty ? true : false;

/** Helper type for improved readability. */
type RequiresGroup<TGrouping> = TGrouping extends Message.Grouping.Grouped
  ? true
  : false;
